import { useEffect, useRef } from "react";

interface UsePlayerAutoCollapseProps {
  isExpanded: boolean;
  onCollapse: () => void;
}

export function usePlayerAutoCollapse({
  isExpanded,
  onCollapse,
}: UsePlayerAutoCollapseProps) {
  const playerRef = useRef<HTMLDivElement>(null);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any existing inactivity timeout
  const clearInactivityTimeout = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  };

  // Start the inactivity timeout
  const startInactivityTimeout = () => {
    clearInactivityTimeout();
    inactivityTimeoutRef.current = setTimeout(() => {
      onCollapse();
    }, 5000); // 5 seconds
  };

  useEffect(() => {
    if (!isExpanded) {
      clearInactivityTimeout();
      return;
    }

    // Start inactivity timer when expanded
    startInactivityTimeout();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        playerRef.current &&
        !playerRef.current.contains(event.target as Node)
      ) {
        onCollapse();
      }
    };

    const handleUserActivity = () => {
      // Reset the inactivity timer on any user activity within the player
      startInactivityTimeout();
    };

    // Add a small delay to prevent immediate collapse when expanding
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);

      // Listen for user activity within the player to reset the timer
      if (playerRef.current) {
        playerRef.current.addEventListener("click", handleUserActivity);
        playerRef.current.addEventListener("mousemove", handleUserActivity);
        playerRef.current.addEventListener("keydown", handleUserActivity);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      clearInactivityTimeout();
      document.removeEventListener("mousedown", handleClickOutside);

      if (playerRef.current) {
        playerRef.current.removeEventListener("click", handleUserActivity);
        playerRef.current.removeEventListener("mousemove", handleUserActivity);
        playerRef.current.removeEventListener("keydown", handleUserActivity);
      }
    };
  }, [isExpanded, onCollapse]);

  return { playerRef };
}
